import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PWAInstall {
  /** App is running installed (standalone display or iOS standalone). */
  isStandalone: boolean;
  /** iOS Safari — cannot prompt programmatically; show manual instructions. */
  isIOS: boolean;
  /** A deferred beforeinstallprompt is available (Android/Chromium). */
  canPrompt: boolean;
  /** Trigger the native install prompt (Android/Chromium). Resolves to the outcome. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)');
  // navigator.standalone is iOS-only (non-standard)
  const iosStandalone = (window.navigator as any).standalone === true;
  return Boolean(mql?.matches || iosStandalone);
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function usePWAInstall(): PWAInstall {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const isIOS = detectIOS();

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    const mql = window.matchMedia?.('(display-mode: standalone)');
    const onChange = () => setIsStandalone(detectStandalone());
    mql?.addEventListener?.('change', onChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      mql?.removeEventListener?.('change', onChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    return outcome;
  }, [deferred]);

  return { isStandalone, isIOS, canPrompt: Boolean(deferred), promptInstall };
}
